import { Button, Descriptions, Space, Tag, message, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { orderService } from '../services/orderService';
import { userService } from '../services/userService';
import type { Order } from '../types/order';

const isAuthenticated = localStorage.getItem('isAuthenticated8Am') === 'true';

const OrderDetail: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth <= 400;

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (id) {
            fetchOrderDetails();
        }
    }, [id]);

    const fetchOrderDetails = async () => {
        try {
            const orderData = await orderService.getOrderById(id!);
            setOrder(orderData);
            const userData = await userService.getUserById(orderData.userId);
            setSelectedUser(userData);
        } catch (error) {
            console.error('Error fetching order details:', error);
            message.error('Không thể tải thông tin đơn hàng');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'waiting':
                return 'gold';
            case 'confirmed':
                return 'blue';
            case 'shipping':
                return 'purple';
            case 'delivered':
                return 'green';
            case 'paid':
                return 'cyan';
            case 'cancelled':
                return 'red';
            default:
                return 'default';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'waiting':
                return 'Đang chờ xác nhận';
            case 'confirmed':
                return 'Đã xác nhận';
            case 'shipping':
                return 'Đang giao hàng';
            case 'delivered':
                return 'Giao hàng thành công';
            case 'paid':
                return 'Đã thanh toán';
            case 'cancelled':
                return 'Đã hủy đơn';
            default:
                return status;
        }
    };

    const mobileStyles = {
        container: {
            padding: '12px',
            maxWidth: '100%',
            margin: '0 auto',
            background: '#f5f5f5'
        },
        header: {
            background: '#fff',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '12px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        },
        section: {
            background: '#fff',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '12px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        },
        sectionTitle: {
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '12px',
            color: '#1890ff',
            borderBottom: '1px solid #f0f0f0',
            paddingBottom: '8px'
        },
        productItem: {
            background: '#f8f8f8',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '8px'
        },
        productName: {
            fontSize: '15px',
            fontWeight: 'bold',
            marginBottom: '8px'
        },
        productDetail: {
            fontSize: '13px',
            color: '#666',
            marginBottom: '4px'
        },
        price: {
            color: '#f50',
            fontWeight: 'bold',
            fontSize: '14px',
            marginTop: '8px'
        },
        totalPrice: {
            fontSize: '18px',
            color: '#f50',
            fontWeight: 'bold',
            marginTop: '8px'
        },
        label: {
            color: '#666',
            fontSize: '13px'
        },
        value: {
            fontSize: '14px',
            color: '#000'
        },
        productImage: {
            width: '60px',
            height: '60px',
            objectFit: 'cover' as const,
            borderRadius: '4px',
            marginRight: '8px'
        },
        productContent: {
            flex: 1
        },
        productRow: {
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: '8px'
        }
    };

    const loadingContainerStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#fff'
    };

    if (loading) {
        return (
            <div style={loadingContainerStyle}>
                <Spin size="large" tip="Đang tải thông tin đơn hàng..." />
            </div>
        );
    }

    if (!order) {
        return (
            <div style={loadingContainerStyle}>
                <div style={{ textAlign: 'center' }}>
                    <h3>Không tìm thấy đơn hàng</h3>
                    {isAuthenticated && (
                        <Button onClick={() => navigate(-1)} style={{ marginTop: 16 }}>
                            Quay lại
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            padding: isMobile ? '16px' : '24px',
            maxWidth: '1200px',
            margin: '0 auto',
            background: '#fff'

        }}>
            {
                !isMobile && (
                    <div style={{
                        marginBottom: '24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '16px'
            }}>
                <h1 style={{
                    margin: 0,
                    fontSize: isMobile ? '16px' : '24px',
                    fontWeight: 'bold'
                }}>Chi tiết đơn hàng #{order.id}</h1>
                {isAuthenticated && (
                        <Button onClick={() => navigate(-1)}>Quay lại</Button>
                    )}
                </div>
            )}

            {isMobile ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div style={mobileStyles.header}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ margin: 0, fontSize: '16px' }}>Đơn hàng #{order.id}</h2>
                            {isAuthenticated && (
                                <Button size="small" onClick={() => navigate(-1)}>Quay lại</Button>
                            )}
                        </div>
                        <div style={{ marginTop: '12px' }}>
                            <Tag color={getStatusColor(order.status)} style={{ margin: 0 }}>
                                {getStatusText(order.status)}
                            </Tag>
                        </div>
                    </div>

                    <div style={mobileStyles.section}>
                        <div style={mobileStyles.sectionTitle}>Thông tin khách hàng</div>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <div>
                                <span style={mobileStyles.label}>Tên: </span>
                                <span style={mobileStyles.value}>{selectedUser?.name}</span>
                            </div>
                            <div>
                                <span style={mobileStyles.label}>SĐT: </span>
                                <span style={mobileStyles.value}>{selectedUser?.phoneNumber?.replace('84', '0')}</span>
                            </div>
                        </Space>
                    </div>

                    <div style={mobileStyles.section}>
                        <div style={mobileStyles.sectionTitle}>Địa chỉ giao hàng</div>
                        <div style={mobileStyles.value}>
                            {order.shippingInfo.address}, {order.shippingInfo.ward},
                            {order.shippingInfo.district}, {order.shippingInfo.province}
                        </div>
                    </div>

                    <div style={mobileStyles.section}>
                        <div style={mobileStyles.sectionTitle}>Sản phẩm</div>
                        {order.items.map((item: any, index: any) => (
                            <div key={index} style={mobileStyles.productItem}>
                                <div style={mobileStyles.productRow}>
                                    <img
                                        src={item.imageUrl}
                                        alt={item.name}
                                        style={mobileStyles.productImage}
                                    />
                                    <div style={mobileStyles.productContent}>
                                        <div style={mobileStyles.productName}>{item.name}</div>
                                        <div style={mobileStyles.productDetail}>Số lượng: {item.quantity}</div>
                                        {item.coffeeId && (
                                            <>
                                                <div style={mobileStyles.productDetail}>Khối lượng: {item.weight}g</div>
                                                <div style={mobileStyles.productDetail}>
                                                    Loại xay: {item.grindType === 'ground' ? 'Xay sẵn' : 'Nguyên hạt'}
                                                    {item.grindType === 'ground' && ` - Độ mịn: ${item.grindSize}`}
                                                </div>
                                            </>
                                        )}
                                        {item.drinkId && (
                                            <div style={mobileStyles.productDetail}>Thể tích: {item.volume}ml</div>
                                        )}
                                        <div style={mobileStyles.price}>{item.price.toLocaleString()}đ</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={mobileStyles.section}>
                        <div style={mobileStyles.sectionTitle}>Thanh toán</div>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <div>
                                <span style={mobileStyles.label}>Phương thức: </span>
                                <span style={mobileStyles.value}>
                                    {order.paymentMethod === 'COD' ? 'Thanh toán khi nhận hàng' : 'Chuyển khoản'}
                                </span>
                            </div>
                            <div style={mobileStyles.totalPrice}>
                                Tổng tiền: {order.totalAmount.toLocaleString()}đ
                            </div>
                        </Space>
                    </div>

                    <div style={mobileStyles.section}>
                        <div style={mobileStyles.sectionTitle}>Thời gian đặt hàng</div>
                        <div style={mobileStyles.value}>
                            {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString('vi-VN', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                            }) : 'N/A'}
                        </div>
                    </div>

                    {order.note && (
                        <div style={mobileStyles.section}>
                            <div style={mobileStyles.sectionTitle}>Ghi chú</div>
                            <div style={mobileStyles.value}>{order.note}</div>
                        </div>
                    )}
                </Space>
            ) : (
                <Descriptions bordered column={2}>

                    <Descriptions.Item label="Mã đơn hàng" span={2}>
                        {order.id}
                    </Descriptions.Item>

                    <Descriptions.Item label="Trạng thái" span={2}>
                        <Tag color={getStatusColor(order.status)}>
                            {getStatusText(order.status)}
                        </Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="Người đặt">
                        {selectedUser?.name}
                    </Descriptions.Item>

                    <Descriptions.Item label="Số điện thoại">
                        {selectedUser?.phoneNumber?.replace('84', '0')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Địa chỉ giao hàng" span={2}>
                        {order.shippingInfo.address}, {order.shippingInfo.ward}, {order.shippingInfo.district}, {order.shippingInfo.province}
                    </Descriptions.Item>

                    <Descriptions.Item label="Sản phẩm" span={2}>
                        {order.items.map((item: any, index: any) => (
                            <div key={index} style={{
                                padding: '12px',
                                border: '1px solid #f0f0f0',
                                borderRadius: '8px',
                                marginBottom: '8px'
                            }}>
                                <div style={{ display: 'flex', gap: '16px' }}>
                                    <img
                                        src={item.imageUrl}
                                        alt={item.name}
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            objectFit: 'cover',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{item.name}</div>
                                        <Space wrap>
                                            <div>Số lượng: {item.quantity}</div>
                                            {item.coffeeId && (
                                                <>
                                                    <div>Khối lượng: {item.weight}g</div>
                                                    <div>
                                                        Loại xay: {item.grindType === 'ground' ? 'Xay sẵn' : 'Nguyên hạt'}
                                                        {item.grindType === 'ground' && ` - Độ mịn: ${item.grindSize}`}
                                                    </div>
                                                </>
                                            )}
                                            {item.drinkId && (
                                                <div>Thể tích: {item.volume}ml</div>
                                            )}
                                            <div style={{ color: '#f50', fontWeight: 'bold' }}>
                                                Giá: {item.price.toLocaleString()}đ
                                            </div>
                                        </Space>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Descriptions.Item>

                    <Descriptions.Item label="Phương thức thanh toán">
                        {order.paymentMethod === 'COD' ? 'Thanh toán khi nhận hàng' : 'Chuyển khoản'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Tổng tiền">
                        <span style={{ color: '#f50', fontWeight: 'bold' }}>
                            {order.totalAmount.toLocaleString()}đ
                        </span>
                    </Descriptions.Item>

                    <Descriptions.Item label="Phí vận chuyển">
                        <span style={{ color: '#f50', fontWeight: 'bold' }}>
                            {order.shippingFee.toLocaleString()}đ
                        </span>
                    </Descriptions.Item>

                    <Descriptions.Item label="Số tiền phải thanh toán">
                        <span style={{ color: '#f50', fontWeight: 'bold' }}>
                            {(order.totalAmount + order.shippingFee).toLocaleString()}đ
                        </span>
                    </Descriptions.Item>

                    <Descriptions.Item label="Thời gian đặt hàng" span={2}>
                        {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString('vi-VN', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                        }) : 'N/A'}
                    </Descriptions.Item>

                    {order.note && (
                        <Descriptions.Item label="Ghi chú" span={2}>
                            {order.note}
                        </Descriptions.Item>
                    )}
                </Descriptions>
            )}


        </div>
    );
};

export default OrderDetail; 